import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Pagination, Select, Tooltip, Typography } from '@attentive-platform/stem-ui';

import StateLozenge from './StateLozenge';
import useScopeRecords, { PAGE_SIZE_OPTIONS } from './useScopeRecords';
import { fetchSalesTeamMembers, mapSalesTeam } from './api';
import {
  ALL_USERS_OPTION,
  DATE_OPTIONS,
  OUTCOME_OPTIONS,
  STATE_OPTIONS,
  TYPE_OPTIONS,
  linkChipTone,
  outcomeChipTone,
  typeChipTone
} from './constants';
import { IntakeTabStyles as I, StatusChip, TableStyles as T } from './styles';

const COLUMNS = [
  { key: 'email', label: 'Customer email', sort: 'email' },
  { key: 'company', label: 'Company', sort: 'company' },
  { key: 'type', label: 'Type', sort: 'recordTypeLabel' },
  { key: 'state', label: 'State', sort: 'state' },
  { key: 'link', label: 'Link', sort: 'linkStatus' },
  { key: 'emails', label: 'Emails', sort: 'emailsEnabled' },
  { key: 'trades', label: 'Trades scoped', sort: 'trades' },
  { key: 'creator', label: 'Creator', sort: 'creator' },
  { key: 'accountOwner', label: 'Account owner', sort: 'accountOwnerSort' },
  { key: 'technicalOwner', label: 'Technical owner', sort: 'technicalOwnerSort' },
  { key: 'created', label: 'Created', sort: 'createdAt' },
  { key: 'submitted', label: 'Submitted', sort: 'submittedAt' },
  { key: 'aging', label: 'Aging', sort: 'sinceSent' },
  { key: 'outcome', label: 'Outcome', sort: 'outcomeLabel' }
];

const IntakeTab = ({ onOpenRow, onNewIntake, refreshToken, searchLocked = false }) => {
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('All states');
  const [typeFilter, setTypeFilter] = useState('All types');
  const [userFilter, setUserFilter] = useState(ALL_USERS_OPTION.value);
  const [outcomeFilter, setOutcomeFilter] = useState('All outcomes');
  const [dateFilter, setDateFilter] = useState('Last 90 days');
  const [team, setTeam] = useState({ ae: [], se: [], allRoster: [] });
  const [expandedTrades, setExpandedTrades] = useState({});
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });

  const userOptions = useMemo(() => {
    const seen = new Set();
    const opts = [ALL_USERS_OPTION];
    (team.allRoster || []).forEach(u => {
      if (seen.has(u.value)) return;
      seen.add(u.value);
      opts.push({ label: u.label, value: u.value });
    });
    return opts;
  }, [team]);

  useEffect(() => {
    let cancelled = false;
    fetchSalesTeamMembers().then(({ data, error }) => {
      if (cancelled || error) return;
      setTeam(mapSalesTeam(data));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const { rows, count, loading, refetch, page, setPage, pageSize, setPageSize, pageCount } = useScopeRecords({
    query,
    stateFilter,
    typeFilter,
    userFilter,
    outcomeFilter,
    dateFilter,
    sort
  });
  const pageSizeItems = useMemo(
    () => PAGE_SIZE_OPTIONS.map(n => ({ label: `${n} / page`, value: n })),
    []
  );

  useEffect(() => {
    if (refreshToken !== undefined) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const rowsLabel = count === 1 ? '1 intake link' : `${count} intake links`;

  const toggleTrades = (e, id) => {
    e.stopPropagation();
    setExpandedTrades(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const onSort = key => {
    setSort(prev =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  return (
    <T.Wrap>
      <T.Toolbar>
        <T.SearchWide>
          <Input
            size='large'
            placeholder='Search customer email or company'
            value={query}
            onChange={e => setQuery(e.target.value)}
            width='20rem'
            name='intake_list_search'
            inputProps={{
              autoComplete: 'off',
              readOnly: searchLocked,
              'data-1p-ignore': true,
              'data-lpignore': 'true'
            }}
          />
        </T.SearchWide>
        <T.Filters>
          <T.SelectSmall>
            <Select
              size='small'
              variant='std-filled'
              items={STATE_OPTIONS}
              value={stateFilter}
              onChange={setStateFilter}
            />
          </T.SelectSmall>
          <T.SelectMedium>
            <Select
              size='small'
              variant='std-filled'
              items={TYPE_OPTIONS}
              value={typeFilter}
              onChange={setTypeFilter}
            />
          </T.SelectMedium>
          <T.SelectWide>
            <Select size='small' variant='std-filled' items={userOptions} value={userFilter} onChange={setUserFilter} />
          </T.SelectWide>
          <T.SelectMedium>
            <Select
              size='small'
              variant='std-filled'
              items={OUTCOME_OPTIONS}
              value={outcomeFilter}
              onChange={setOutcomeFilter}
            />
          </T.SelectMedium>
          <T.SelectMedium>
            <Select
              size='small'
              variant='std-filled'
              items={DATE_OPTIONS}
              value={dateFilter}
              onChange={setDateFilter}
            />
          </T.SelectMedium>
          <Button variant='contained' color='primary' onClick={onNewIntake}>
            New intake
          </Button>
        </T.Filters>
      </T.Toolbar>

      <T.TableBox>
        <I.HeaderGrid>
          {COLUMNS.map(c => (
            <T.SortHeader key={c.key} type='button' onClick={() => onSort(c.sort)}>
              {c.label}
              <T.SortMark>
                {sort.key === c.sort ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
              </T.SortMark>
            </T.SortHeader>
          ))}
        </I.HeaderGrid>

        {loading ? (
          <T.EmptySmall>Loading intake links…</T.EmptySmall>
        ) : rows.length === 0 ? (
          <T.EmptyBig>
            <Typography variant='body1' sx={{ fontWeight: 600 }}>
              No intake links yet
            </Typography>
            <Typography variant='body3' color='#666666' sx={{ maxWidth: '44ch', textAlign: 'center' }}>
              Send a customer a scope form and it shows up here — with its state and age.
            </Typography>
            <I.EmptyCtaWrap>
              <Button variant='contained' color='primary' onClick={onNewIntake}>
                New intake
              </Button>
            </I.EmptyCtaWrap>
          </T.EmptyBig>
        ) : (
          rows.map(r => {
            const Row =
              r.pilotStatus === 'closed' ||
              r.pilotStatus === 'closed_won' ||
              r.pilotStatus === 'closed_lost'
                ? I.RowClosed
                : I.RowOpen;
            const linkTone = linkChipTone(r.linkStatus);
            const tradesOpen = !!expandedTrades[r.id];
            const submitted = r.state === 'Submitted' || !!r.submittedAt;
            return (
              <Row key={r.id} onClick={() => onOpenRow && onOpenRow(r.id)}>
                <T.CellCol>
                  <I.EmailText>{r.email}</I.EmailText>
                  {r.submittedEmail && <I.MismatchPill>submitted from {r.submittedEmail}</I.MismatchPill>}
                </T.CellCol>
                <I.CellTrunc>
                  <Typography variant='body3'>{r.company}</Typography>
                </I.CellTrunc>
                <T.Cell>
                  <StatusChip tone={typeChipTone(r.isPilot)}>{r.recordTypeLabel}</StatusChip>
                </T.Cell>
                <T.Cell>
                  <StateLozenge state={r.state} />
                </T.Cell>
                <T.Cell>
                  {r.linkStatus ? (
                    <Tooltip
                      arrow
                      placement='top'
                      variant='dark'
                      title={r.linkHover || (r.linkExpiresAt ? `Valid until ${r.linkExpiresAt}` : undefined)}>
                      <span>
                        <StatusChip tone={linkTone}>{r.linkStatus}</StatusChip>
                      </span>
                    </Tooltip>
                  ) : (
                    <Typography variant='body3' color='#9E9E9E'>
                      —
                    </Typography>
                  )}
                </T.Cell>
                <T.Cell>
                  {r.emailsEnabled ? (
                    <Typography variant='body3' color='#4D4D4D'>
                      On
                    </Typography>
                  ) : (
                    <I.EmailsOff>⊘ Off</I.EmailsOff>
                  )}
                </T.Cell>
                <T.Cell>
                  {submitted && r.trades > 0 ? (
                    <>
                      <I.TradesCount type='button' onClick={e => toggleTrades(e, r.id)}>
                        {r.trades}
                      </I.TradesCount>
                      {tradesOpen && <I.TradesExpanded>{r.tradeList.join(', ')}</I.TradesExpanded>}
                    </>
                  ) : (
                    <Typography variant='body3' color='#9E9E9E'>
                      —
                    </Typography>
                  )}
                </T.Cell>
                <T.Cell>
                  <Typography variant='body3'>{r.creator}</Typography>
                </T.Cell>
                <T.CellStack>
                  <Typography variant='body3' color='#4D4D4D'>
                    {r.accountOwner || '—'}
                  </Typography>
                  {r.accountOwnerRole ? (
                    <Typography variant='body5' color='#808080'>
                      {r.accountOwnerRole}
                    </Typography>
                  ) : null}
                </T.CellStack>
                <T.CellStack>
                  <Typography variant='body3' color='#4D4D4D'>
                    {r.technicalOwner || '—'}
                  </Typography>
                  {r.technicalOwnerRole ? (
                    <Typography variant='body5' color='#808080'>
                      {r.technicalOwnerRole}
                    </Typography>
                  ) : null}
                </T.CellStack>
                <T.Cell>
                  <Typography variant='body3' color='#4D4D4D'>
                    {r.created}
                  </Typography>
                </T.Cell>
                <T.Cell>
                  <Typography variant='body3' color='#4D4D4D'>
                    {r.submitted || '—'}
                  </Typography>
                </T.Cell>
                <T.CellStack>
                  {r.sinceSent ? (
                    r.stale ? <I.AgingStale>{r.sinceSent}</I.AgingStale> : <I.AgingCalm>{r.sinceSent}</I.AgingCalm>
                  ) : (
                    <Typography variant='body3' color='#9E9E9E'>
                      —
                    </Typography>
                  )}
                  {r.inState ? (
                    <Typography variant='body3' color='#808080'>
                      {r.inState}
                    </Typography>
                  ) : null}
                </T.CellStack>
                <T.Cell>
                  {r.outcomeLabel && r.outcomeLabel !== '—' ? (
                    <StatusChip
                      tone={outcomeChipTone({
                        isPilot: r.isPilot,
                        outcome: r.outcome,
                        pilotStatus: r.pilotStatus
                      })}>
                      {r.outcomeLabel}
                    </StatusChip>
                  ) : (
                    <Typography variant='body3' color='#9E9E9E'>
                      —
                    </Typography>
                  )}
                </T.Cell>
              </Row>
            );
          })
        )}
      </T.TableBox>

      <T.FooterRow>
        <Typography variant='body3' color='#666666'>
          {rowsLabel}
        </Typography>
        <T.FooterRight>
          <T.SelectSmall>
            <Select
              size='small'
              variant='std-filled'
              items={pageSizeItems}
              value={pageSize}
              onChange={next => setPageSize(Number(next))}
            />
          </T.SelectSmall>
          {pageCount > 1 && (
            <Pagination
              size='small'
              color='primary'
              count={pageCount}
              page={page}
              onChange={(_, next) => setPage(next)}
            />
          )}
        </T.FooterRight>
      </T.FooterRow>
    </T.Wrap>
  );
};

export default IntakeTab;
